import { Button, Drawer, Select } from "antd";
import CommonInputField from "./common_input_field";
import DeleteIcon from "./DeleteIcon";
import NavigationButton from "./navigation_button";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useMemo, useState } from "react";
import { on } from "events";
import { label } from "aws-amplify";

interface Link {
  platform: string;
  url: string;
  icon: React.ReactNode;
}

interface SocialPlatform {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

interface AddNewItemWithUrlProps {
  links: Link[];
  selectedLink?: Link;
  setLinks: (links: Link[]) => void;
  socialPlatforms: SocialPlatform[];
  title: string;
  field: string;
  deleteLabel?: string;
  commonIcon?: React.ReactNode;
  isNew?: boolean;
  openDrawer: boolean;
  onClose?: () => void;
}

const AddNewItemWithUrl: React.FC<AddNewItemWithUrlProps> = ({
  links,
  setLinks,
  socialPlatforms,
  title,
  field,
  commonIcon,
  deleteLabel,
  isNew = false,
  onClose,
  openDrawer,
  selectedLink,
}) => {
  const [newLink, setNewLink] = useState<Link>({ platform: "", url: "", icon: commonIcon || null });
  useEffect(() => {
    if (selectedLink) {
      setNewLink(selectedLink);
    } else {
      setNewLink({ platform: "", url: "", icon: commonIcon || null });
    }
  }, [selectedLink, openDrawer]);

  const getPlatformIcon = (platformValue: string) => {
    const found = socialPlatforms.find(p => p.value === platformValue);
    return found?.icon ?? commonIcon ?? null;
  };

  const handleChange = (key: keyof Link, value: string) => {
    setNewLink(prev => ({
      ...prev,
      [key]: value,
    }));
  };
  const handleSave = () => {
    if (newLink.platform && newLink.url) {
      const newEntry: Link = {
        ...newLink,
        icon: getPlatformIcon(newLink.platform),
      };

      let updatedLinks = [...links];

      if (selectedLink) {
        // Remove the old version before updating
        updatedLinks = updatedLinks.filter((l) => l !== selectedLink);
      }

      updatedLinks.push(newEntry);
      setLinks(updatedLinks);

      setNewLink({ platform: "", url: "", icon: commonIcon || null });
      onClose?.();
    } else {
      console.warn("Platform or URL is empty");
    }
  };


  const handleDelete = (index: number) => {
    setLinks(links.filter((_, i) => i !== index));
    onClose?.();
  };

  const marketplacePlatforms = useMemo(() => {
    return links?.map((m: any) => m?.platform)
  }, [links])


  return (
    <Drawer
      placement="bottom"
      open={openDrawer}
      closeIcon={null}
      onClose={onClose}
      height="60vh"
      title={isNew ? `Add New ${field}` : ""}
      styles={{
        header: { borderBottom: "none", paddingTop: 64, },
        footer: { borderTop: "none" },
        content: {
          borderTopLeftRadius: 20,
          borderTopRightRadius: 20,
          overflow: "hidden",
        },
      }}
      footer={
        <div className="inline-flex justify-between w-full px-4">
          <NavigationButton onClick={onClose || (() => { })} label="Cancel" type="text" block />
          <NavigationButton onClick={handleSave} label="Save" type="primary" block />
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-[14px] items-center">
          {/* Platform Select */}
          <div>
            <label className="block common-label pb-2">{field}</label>
            <Select
              value={newLink.platform || undefined}
              onChange={(value) => handleChange("platform", value)}
              className="w-full h-[40px]"
              placeholder={`Select ${title}`}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.value as string)?.toLowerCase().includes(input.toLowerCase())
              }
              options={socialPlatforms?.filter((o) => !marketplacePlatforms.includes(o?.value))?.map((platform) => ({
                value: platform.value,
                label: (
                  <div className="flex items-center gap-2">
                    <span>{platform.icon ?? commonIcon}</span>
                    <span>{platform.label}</span>
                  </div>
                ),
              }))}
            />
          </div>

          {/* URL Input */}
          <div>
            <CommonInputField
              label="URL"
              value={newLink.url}
              onChange={(e) => handleChange("url", e.target.value)}
            />
          </div>

          {/* Delete Icon (only show if editing an existing one, i.e. !isNew) */}
          {!isNew && (
            <div className="flex md:justify-center items-center mt-6 md:ml-[14px]">
              <Button
                icon={<DeleteIcon />}
                onClick={() => handleDelete(links.findIndex((l) => l === selectedLink))}
                className="p-[5px] hover:border hover:border-gray-100"
                type="text"
              />
              <div className="text-[#667085] text-sm font-medium md:hidden">{deleteLabel ?? ""}</div>
            </div>
          )}
        </div>
      </div>
    </Drawer>
  );
};

export default AddNewItemWithUrl;
